import { useEffect, useRef, useState } from 'react';
import { Phone, Mail, Linkedin } from 'lucide-react';
import { Link } from 'react-router-dom';
import aboutBackground from '../../assets/background/IMG_8346.webp';
import LearnMoreLetters from './LearnMoreLetters';
import useScrollProgress from '../../page_assets/useScrollProgress';

const NAV_ITEMS = [
    { path: '/art-portfolio', label: 'Art Portfolio' },
    { path: '/cs-portfolio', label: 'Computer Science Portfolio' },
    { path: '/CV_catherinebao.pdf', label: 'Curriculum Vitae', isExternal: true },
];

const CONTACTS = [
    { href: 'tel:8016366104', icon: Phone, label: 'Phone' },
    { href: 'mailto:catherine.1bao@gmail.com', icon: Mail, label: 'Email' },
    { href: 'https://www.linkedin.com/in/catherineba0/', icon: Linkedin, label: 'LinkedIn', external: true },
];

const PILL_CLASS = 'block w-full bg-transparent text-[#F7DB00] border border-[#F7DB00] ' +
    'hover:bg-[#F7DB00]/10 hover:text-[#FFEB5C] hover:border-[#FFEB5C] ' +
    'py-2 px-5 rounded-full transition-colors duration-300 text-left';

const ICON_CLASS = 'text-[#F7DB00] hover:text-[#FFEB5C] transition-colors';

// Viewports of scroll the letters take to assemble. The track's own height is written
// as a literal class below and has to stay one viewport taller than this — Tailwind
// only sees arbitrary values it can read as static strings, so it can't be derived.
const ASSEMBLY_VIEWPORTS = 1.25;

// The flat field IMG_8346 is painted on, sampled off the photo itself — so the blue the
// mobile layout runs above the photo is the photo's own, and the two meet without a seam.
const FIELD = '#002180';

// The same blue as the "r, g, b" list the header takes its fill from over this section —
// see page_assets/header.
export const FIELD_RGB = '0, 33, 128';

/**
 * Second full-bleed screen: where to go next, laid out around the background photo.
 */
export default function AboutSection() {
    const trackRef = useRef(null);
    const { scrollY, viewportHeight } = useScrollProgress();
    const [trackTop, setTrackTop] = useState(0);

    // offsetTop rather than a live getBoundingClientRect: the track's distance from the
    // top of the document only changes when the layout does, so measuring it on mount
    // and on resize keeps the per-scroll work to arithmetic.
    useEffect(() => {
        const measure = () => setTrackTop(trackRef.current?.offsetTop ?? 0);
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, []);

    const letterProgress = (scrollY - trackTop) / (viewportHeight * ASSEMBLY_VIEWPORTS);

    return (
        // One viewport taller than the letters' journey (so: ASSEMBLY_VIEWPORTS + 1),
        // with the panel pinned inside — that surplus is what gives room to scroll
        // *through* this section rather than past it. Below lg the track collapses and
        // the section is an ordinary block again, since the content stacks and grows.
        <div ref={trackRef} className="relative lg:h-[300vh] snap-start">
            {/* The stop for the assembled state. Without it, mandatory snapping treats
                the top of the track as the only stop here and one gesture would carry
                straight past the letters into the footer. */}
            <div
                aria-hidden="true"
                className="hidden lg:block absolute left-0 w-px h-px snap-start pointer-events-none"
                style={{ top: `${ASSEMBLY_VIEWPORTS * 100}vh` }}
            />

        <section
            id="about-section"
            className="relative w-full min-h-screen lg:h-screen lg:sticky lg:top-0 lg:overflow-hidden flex flex-col items-center justify-center pt-[5vh] pb-[124vw] lg:py-0"
            style={{ backgroundColor: FIELD }}
        >
            {/* The photo is the section's surface at both sizes, but fitted differently.
                lg crops it to fill the screen; below that it is blown up to 240% of the
                screen's width and sat on the bottom edge, so a phone gets one leaf at
                scale rather than the whole picture as a strip along the foot of the page.
                Flipped so the mirrored crop puts the leaf's sweep on the right and the
                three dots out on the left, where nothing else in the section is. The flat
                blue behind it is the photo's own field colour, and that extension is the
                room everything else is laid out in. The section's pb clears the leaves
                rather than the whole photo — the top ~28% of the frame is empty sky, so
                the links sit over that much of it with only blue behind them. */}
            <div
                aria-hidden="true"
                className="absolute inset-0 bg-[length:240%_auto] bg-bottom bg-no-repeat -scale-x-100 lg:bg-cover lg:bg-center lg:scale-x-100"
                style={{ backgroundImage: `url(${aboutBackground})` }}
            />
            <LearnMoreLetters progress={letterProgress} />

            {/* Mobile: the word gets a band of its own at the top of the section. It is
                the band, not padding above it, that sets how far down the links start —
                the word sits near the band's top edge, so the two can be moved
                independently. */}
            <div className="lg:hidden relative w-full shrink-0 h-[22vh] min-h-[8rem]">
                <LearnMoreLetters variant="mobile" />
            </div>

            {/* Deliberately NOT `relative`: the link column below is absolutely placed
                against the <section> so its percentages track the viewport — and so the
                full-bleed background — rather than this centered, max-width wrapper.
                z-10 still applies here because the wrapper is a flex item. */}
            <div className="z-10 w-full max-w-6xl lg:max-w-7xl lg:min-h-screen px-4 sm:px-6 lg:px-8 pb-2 lg:py-24">
                <div className="flex flex-col lg:flex-row lg:items-start lg:min-h-[calc(100vh-12rem)] gap-2 lg:gap-12">
                    {/* <div className="lg:w-1/2">
                        <h1
                        className="text-4xl sm:text-4xl lg:text-6xl font-bold mb-6 text-[#0008ff]"
                        style={{ fontFamily: '"Nanum Pen Script", cursive' }}
                        >
                            About Me
                        </h1>
                         <img src={require('../../assets/self.webp')} alt="Catherine Bao head shot" className="w-full max-w-md mx-auto lg:mx-0 rounded-lg"/>
                    </div> */}

                    {/* The three dots in IMG_8346 sit at roughly 67% across and 38–49%
                        down the photo. bg-cover crops height, not width, on any viewport
                        wider than the photo's 3:2, so 68% of the viewport width lands
                        just right of them and 44% down tracks their vertical middle. */}
                    <div className="lg:absolute lg:left-[72%] lg:top-[44%] lg:-translate-y-1/2 lg:w-[22%] lg:min-w-[15rem] lg:max-w-[18rem]">
                        <div className="space-y-3">
                            {NAV_ITEMS.map((item) => (
                                item.isExternal ? (
                                    <a
                                        key={item.path}
                                        href={item.path}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={PILL_CLASS}
                                    >
                                        <p className="text-sm font-semibold">{item.label}</p>
                                    </a>
                                ) : (
                                    <Link key={item.path} to={item.path} className={PILL_CLASS}>
                                        <p className="text-sm font-semibold">{item.label}</p>
                                    </Link>
                                )
                            ))}
                        </div>
                    </div>

                    {/* DOM order stays links-then-icons for the stacked mobile layout;
                        lg:order-first moves the icons left only on desktop, where the
                        links are absolutely placed anyway. */}
                    <div className="lg:w-1/3 lg:order-first lg:self-end">
                        <div className="flex gap-4 sm:gap-6 items-center justify-start py-1 sm:py-8">
                            {CONTACTS.map(({ href, icon: Icon, label, external }) => (
                                <a
                                    key={label}
                                    href={href}
                                    className={ICON_CLASS}
                                    aria-label={label}
                                    {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                                >
                                    <Icon className="w-6 h-6 sm:w-8 sm:h-8" />
                                </a>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

        </section>
        </div>
    );
}
